import Button from "@rio-cloud/rio-uikit/Button";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useMatch } from "react-router";

import ExampleQuestionsFilterList from "@/components/molecule/ExampleQuestionsFilterList/ExampleQuestionsFilterList";
import {
    ExampleQuestions,
    ExampleQuestionsContainer,
    FilterListContainer,
} from "@/components/organism/ExampleQuestionsList/ExampleQuestionsList.styles";
import { THandleSendParams } from "@/components/pages/ChatPage/types";
import { useAppSelector } from "@/configuration/setup/hooks";
import { isCurrentRoute } from "@/routes/routeHelper";
import {
    selectIsDemoFlowEnabled,
    selectIsOnboardingMode,
    selectOnboardingStep,
} from "@/store/onboarding/onboardingSlice";
import { selectActiveThread, selectIsThreadLoading } from "@/store/thread/threadSlice";

type ExampleQuestionsListProps = {
    isLoading?: boolean;
    handleSend?: (params: THandleSendParams) => Promise<void>;
    shouldShowEmptyState?: boolean;
};

const ExampleQuestionsList = ({
                                  isLoading,
                                  handleSend,
                                  shouldShowEmptyState,
                              }: ExampleQuestionsListProps) => {
    const [simulateHover, setSimulateHover] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const isOnboardingMode = useAppSelector(selectIsOnboardingMode);
    const enableDemoFlow = useAppSelector(selectIsDemoFlowEnabled);
    const onboardingStep = useAppSelector(selectOnboardingStep);
    const isOnBoardingLocation = isCurrentRoute("/onboarding");
    const activeThread = useAppSelector(selectActiveThread);
    const isThreadsLoading = useAppSelector(selectIsThreadLoading(activeThread?.threadId ?? null));
    const isChatPage = useMatch("/chat");
    const filterListRef = useRef<HTMLDivElement>(null);

    const toggleFilterList = () => setIsVisible((prev) => !prev);

    useEffect(() => {
        setSimulateHover(false);

        if ((!isChatPage || isThreadsLoading) && !(isOnboardingMode && enableDemoFlow)) {
            setIsVisible(false);
            return;
        }

        if (enableDemoFlow && isOnboardingMode) {
            setIsVisible(onboardingStep === "step-0");
            return;
        }

        if (!shouldShowEmptyState && isVisible) {
            setIsVisible(false);
        }

        if (shouldShowEmptyState) {
            const hoverTimer = setTimeout(() => {
                setSimulateHover(true);
                const clickTimer = setTimeout(() => {
                    setSimulateHover(false);
                    setIsVisible(true);
                }, 300);
                return () => clearTimeout(clickTimer);
            }, 1000);
            return () => clearTimeout(hoverTimer);
        }
    }, [
        activeThread?.threadId,
        isChatPage,
        isThreadsLoading,
        enableDemoFlow,
        isOnboardingMode,
        onboardingStep,
        shouldShowEmptyState,
    ]);

    return (
        <ExampleQuestionsContainer>
            {isVisible && (
                <FilterListContainer data-testid="example-questions-panel" ref={filterListRef} className="visible">
                    <ExampleQuestionsFilterList
                        handleSend={handleSend}
                        isOnboarding={enableDemoFlow && isOnboardingMode}
                        isLoading={isLoading}
                    />
                </FilterListContainer>
            )}
            <ExampleQuestions>
                <Button
                    data-testid="example-questions-button"
                    iconName="rioglyph-question-sign"
                    onClick={toggleFilterList}
                    disabled={isLoading || isOnBoardingLocation}
                    className={clsx("btn", "btn-primary", "btn-link", {
                        "btn-default focus": simulateHover,
                    })}
                >
                    <FormattedMessage id="chatPage.sidebar.faq.label" />
                </Button>
            </ExampleQuestions>
        </ExampleQuestionsContainer>
    );
};

export default ExampleQuestionsList;
